'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import SiteHeader from '@/components/SiteHeader';
import SiteFooter from '@/components/SiteFooter';
import PageHeader from '@/components/shared/PageHeader';
import SearchBar from '@/components/shared/SearchBar';
import FilterRow from '@/components/shared/FilterRow';
import Badge from '@/components/shared/Badge';
import EmptyState from '@/components/shared/EmptyState';
import { ServiceCardSkeleton } from '@/components/shared/Skeletons';
import { mockServices } from '@/data/mockServices';
import { Briefcase, Lock, ArrowRight } from 'lucide-react';

const categoryFilters = [
  { label: 'All', value: 'all' },
  { label: 'Fitness', value: 'Fitness' },
  { label: 'Interior Design', value: 'Interior Design' },
  { label: 'Cleaning', value: 'Cleaning' },
  { label: 'Pet Care', value: 'Pet Care' },
  { label: 'Wellness', value: 'Wellness' },
  { label: 'Photography', value: 'Photography' },
  { label: 'Real Estate', value: 'Real Estate' },
];

function StarRating({ rating, count }: { rating: number; count: number }) {
  return (
    <div className="flex items-center gap-1.5">
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <svg key={star} width="11" height="11" viewBox="0 0 12 12" fill="none">
            <path
              d="M6 1L7.35 4.36L11 4.64L8.5 6.86L9.27 10.5L6 8.5L2.73 10.5L3.5 6.86L1 4.64L4.65 4.36L6 1Z"
              fill={star <= Math.round(rating) ? '#F0A832' : 'rgba(167,163,196,0.20)'}
            />
          </svg>
        ))}
      </div>
      <span className="text-[12px] font-semibold tabular-nums" style={{ color: '#F0A832' }}>{rating.toFixed(1)}</span>
      <span className="text-[12px]" style={{ color: 'rgba(167,163,196,0.55)' }}>({count})</span>
    </div>
  );
}

function AvatarInitials({ name }: { name: string }) {
  const initials = name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase();
  return (
    <div
      className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 font-display font-bold text-[12px]"
      style={{
        background: 'linear-gradient(135deg, rgba(76,201,255,0.20), rgba(122,92,255,0.20))',
        border: '1px solid rgba(76,201,255,0.20)',
        color: '#4CC9FF',
      }}
    >
      {initials}
    </div>
  );
}

export default function ServicesPage() {
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [isLoading] = useState(false);

  const filtered = mockServices.filter((s) => {
    const matchesSearch =
      !search ||
      s.title.toLowerCase().includes(search.toLowerCase()) ||
      s.providerName.toLowerCase().includes(search.toLowerCase()) ||
      s.category.toLowerCase().includes(search.toLowerCase());
    const matchesFilter = activeFilter === 'all' || s.category === activeFilter;
    return matchesSearch && matchesFilter;
  });

  // Mock: not approved — show teaser state
  const isApproved = false;

  return (
    <div className="min-h-screen" style={{ background: '#07060C' }}>
      <SiteHeader theme="dark" />
      <main className="pt-16">
        <PageHeader
          eyebrow="Services"
          title="Resident Services"
          description="Services offered by verified Panorama residents and approved sponsors. All providers are part of the community."
          stat={{ value: `${mockServices.length}`, label: 'Active listings' }}
        />

        {/* Private launch teaser banner */}
        {!isApproved && (
          <div
            className="border-b"
            style={{ background: 'rgba(122,92,255,0.04)', borderColor: 'rgba(122,92,255,0.15)' }}
          >
            <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <Lock size={14} style={{ color: '#9B7FFF', flexShrink: 0 }} />
                <p className="text-[13px] leading-relaxed" style={{ color: 'rgba(167,163,196,0.80)' }}>
                  <span style={{ color: '#F2F4FF' }}>Resident Services</span> is part of the private Panorama network. Access is rolling out in waves for verified residents.
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Link href="/request-access" className="inline-flex items-center gap-1.5 text-[12px] font-semibold px-3 py-1.5 rounded-lg transition-all duration-150" style={{ background: 'rgba(122,92,255,0.15)', border: '1px solid rgba(122,92,255,0.30)', color: '#C4BEFF' }}>
                  Request Access <ArrowRight size={11} />
                </Link>
                <Link href="/login" className="text-[12px] font-medium px-3 py-1.5 rounded-lg transition-all duration-150" style={{ color: 'rgba(167,163,196,0.60)', border: '1px solid rgba(255,255,255,0.08)' }}>
                  Sign In
                </Link>
              </div>
            </div>
          </div>
        )}

        <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 py-8">
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="flex-1 max-w-sm">
              <SearchBar placeholder="Search services, providers..." value={search} onChange={setSearch} />
            </div>
          </div>
          <div className="mb-8">
            <FilterRow filters={categoryFilters} active={activeFilter} onChange={setActiveFilter} />
          </div>

          {isLoading && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {Array.from({ length: 6 }).map((_, i) => <ServiceCardSkeleton key={i} />)}
            </div>
          )}

          {!isLoading && filtered.length === 0 && (
            <EmptyState
              title="No services found"
              description="Try adjusting your search or category filter."
              icon={<Briefcase size={22} style={{ color: '#4CC9FF' }} />}
              action={
                <button onClick={() => { setSearch(''); setActiveFilter('all'); }} className="btn-secondary text-[13px] px-4 py-2">
                  Clear filters
                </button>
              }
            />
          )}

          {!isLoading && filtered.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filtered.map((service) => (
                <Link key={service.id} href={`/services/${service.slug}`} className="block group focus-visible:outline-none">
                  <div className="neon-card p-5 h-full cursor-pointer group-focus-visible:border-[rgba(76,201,255,0.35)] group-focus-visible:shadow-[0_0_0_3px_rgba(76,201,255,0.15)]" style={!isApproved ? { opacity: 0.55, pointerEvents: 'none', userSelect: 'none' } : {}}>
                    {/* Header */}
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1.5">
                          <span
                            className="text-[11px] px-2 py-0.5 rounded font-medium"
                            style={{ background: 'rgba(76,201,255,0.08)', color: '#4CC9FF', border: '1px solid rgba(76,201,255,0.15)' }}
                          >
                            {service.category}
                          </span>
                          {service.isSponsor && <Badge variant="sponsor" />}
                        </div>
                        <h3 className="font-display font-semibold text-[15px] leading-snug" style={{ color: '#F2F4FF' }}>
                          {service.title}
                        </h3>
                      </div>
                    </div>

                    {/* Provider */}
                    <div className="flex items-center gap-2.5 mb-3">
                      <AvatarInitials name={service.providerName} />
                      <div>
                        <p className="text-[13px] font-medium" style={{ color: 'rgba(242,244,255,0.80)' }}>
                          {service.providerName}
                        </p>
                        <p className="text-[11.5px]" style={{ color: '#A7A3C4' }}>
                          {service.isSponsor ? 'Sponsor' : `${service.providerRole} · ${service.providerTower}`}
                        </p>
                      </div>
                    </div>

                    {/* Description */}
                    <p className="text-[13px] leading-relaxed mb-3 line-clamp-2" style={{ color: 'rgba(167,163,196,0.80)' }}>
                      {service.description}
                    </p>

                    {/* Footer */}
                    <div className="flex items-center justify-between pt-3" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                      <StarRating rating={service.rating} count={service.reviewCount} />
                      {service.rate && (
                        <span className="text-[12px] font-medium" style={{ color: 'rgba(242,244,255,0.60)' }}>
                          {service.rate.split('·')[0].trim()}
                        </span>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}

          {/* Cross-links */}
          <div
            className="mt-16 pt-8 border-t"
            style={{ borderColor: 'rgba(255,255,255,0.06)' }}
          >
            <p className="text-[11px] font-semibold tracking-[0.10em] uppercase mb-4" style={{ color: 'rgba(167,163,196,0.35)' }}>
              Also in the community
            </p>
            <div className="flex flex-wrap gap-3">
              <Link href="/directory" className="text-[13px] font-medium px-4 py-2 rounded-lg transition-all duration-150 hover:bg-white/[0.06]" style={{ color: '#A7A3C4', border: '1px solid rgba(255,255,255,0.08)' }}>
                Resident Directory
              </Link>
              <Link href="/conversations" className="text-[13px] font-medium px-4 py-2 rounded-lg transition-all duration-150 hover:bg-white/[0.06]" style={{ color: '#A7A3C4', border: '1px solid rgba(255,255,255,0.08)' }}>
                Conversations
              </Link>
              <Link href="/marketplace" className="text-[13px] font-medium px-4 py-2 rounded-lg transition-all duration-150 hover:bg-white/[0.06]" style={{ color: '#A7A3C4', border: '1px solid rgba(255,255,255,0.08)' }}>
                Marketplace
              </Link>
              <Link href="/resources" className="text-[13px] font-medium px-4 py-2 rounded-lg transition-all duration-150 hover:bg-white/[0.06]" style={{ color: '#A7A3C4', border: '1px solid rgba(255,255,255,0.08)' }}>
                Resources
              </Link>
            </div>
          </div>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
